import { CheckCircle, ErrorOutline, Groups } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../components/layout/AppHeader';
import API_ENDPOINTS from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface InvitePreview {
  provider: {
    id: string;
    business_name: string;
  };
  site: {
    id: string;
    name: string;
  };
  expires_at: string;
  can_redeem?: boolean;
  detail?: string | null;
  retry_after_seconds?: number | null;
  // Reason: backend stamps the current Provider T&Cs / Privacy versions on the
  // preview so the redeem payload echoes the exact strings the user accepted.
  provider_terms_version?: string;
  provider_privacy_version?: string;
}

const FONT_FAMILY =
  'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

const formatRetryAfter = (seconds: number): string => {
  const hours = Math.ceil(seconds / 3600);
  if (hours >= 24) {
    const days = Math.ceil(hours / 24);
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }
  if (hours >= 1) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
};

const JoinPage: React.FC = () => {
  const { secret } = useParams<{ secret: string }>();
  const navigate = useNavigate();
  const { refreshUser, user, loading: authLoading } = useAuth();

  // Reason: unauthenticated visitors land here from a Provider invite
  // email/QR/link. The preview + redeem endpoints both require an
  // authenticated user, so an unauth visit would just dead-end with
  // 401s. Redirect them to /register?invite=<secret> so the signup
  // form can carry the secret through to the backend (attribution
  // stamp on User.signed_up_via_invite_token), and direct them to
  // /login as the obvious "I already have an account" affordance.
  // Once authenticated they come back here naturally via the
  // redeem flow on Profile / their own re-tap of the invite link.
  useEffect(() => {
    if (authLoading) return;
    if (!user && secret) {
      navigate(
        `/register?invite=${encodeURIComponent(secret)}&next=${encodeURIComponent(
          `/join/${secret}`,
        )}`,
        { replace: true },
      );
    }
  }, [authLoading, user, secret, navigate]);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const shouldPromptProfileLeave =
    preview?.can_redeem === false &&
    preview?.detail?.includes('active Provider/Site membership');

  const getPreviewMessage = (): string | null => {
    if (!preview?.detail) {
      return null;
    }
    const retryAfterText =
      typeof preview.retry_after_seconds === 'number'
        ? ` Please try again in ${formatRetryAfter(preview.retry_after_seconds)}.`
        : '';
    return `${preview.detail}${retryAfterText}`;
  };

  useEffect(() => {
    const loadPreview = async () => {
      if (!secret) {
        setError('Invite token is missing.');
        setLoading(false);
        return;
      }
      // Reason: skip preview while we wait to know auth state, and skip
      // entirely for unauthenticated visitors — they're being redirected
      // to /register by the effect above and a 401 from the preview
      // endpoint would just flash a misleading error in the meantime.
      if (authLoading || !user) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await axios.post(API_ENDPOINTS.AUTH.PROVIDER_INVITE_PREVIEW, {
          secret,
        });
        setPreview(response.data);
      } catch (err: any) {
        setError(
          err.response?.data?.secret?.[0] ||
          err.response?.data?.detail ||
          'This invite could not be found or is no longer valid.',
        );
      } finally {
        setLoading(false);
      }
    };

    loadPreview();
  }, [secret, user, authLoading]);

  const handleRedeem = async () => {
    if (!secret) {
      return;
    }

    try {
      setRedeeming(true);
      setError(null);
      setSuccess(null);
      await axios.post(API_ENDPOINTS.AUTH.PROVIDER_INVITE_REDEEM, {
        secret,
        accepted_terms_version: preview?.provider_terms_version ?? '',
        accepted_privacy_version: preview?.provider_privacy_version ?? '',
      });
      await refreshUser();
      setSuccess('You have joined this provider.');
    } catch (err: any) {
      const retryAfterSeconds = err.response?.data?.retry_after_seconds;
      const retryAfterText =
        typeof retryAfterSeconds === 'number'
          ? ` Please try again in ${formatRetryAfter(retryAfterSeconds)}.`
          : '';
      setError(
        `${err.response?.data?.detail || ''}${retryAfterText}` ||
        err.response?.data?.secret?.[0] ||
        'This invite could not be redeemed.',
      );
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <Box sx={{ minHeight: 'var(--app-height, 100vh)', bgcolor: '#F5F7FB' }}>
      <AppHeader title="Join Provider" />
      <Box sx={{ p: 2, maxWidth: 520, mx: 'auto' }}>
        <Card sx={{ borderRadius: '24px', mt: 2 }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            {loading ? (
              <Box sx={{ py: 6 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2, fontFamily: FONT_FAMILY }}>
                  Checking your invite...
                </Typography>
              </Box>
            ) : error ? (
              <Box sx={{ py: 3 }}>
                <ErrorOutline color="error" sx={{ fontSize: 48, mb: 2 }} />
                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                  {error}
                </Alert>
                <Button variant="contained" onClick={() => navigate('/home')}>
                  Back to Home
                </Button>
              </Box>
            ) : success ? (
              <Box sx={{ py: 3 }}>
                <CheckCircle color="success" sx={{ fontSize: 56, mb: 2 }} />
                <Typography
                  variant="h5"
                  sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, mb: 1 }}
                >
                  You're in
                </Typography>
                <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                  {success}
                </Alert>
                <Button variant="contained" onClick={() => navigate('/home')}>
                  Continue
                </Button>
              </Box>
            ) : preview ? (
              <Box sx={{ py: 3 }}>
                <Groups color="primary" sx={{ fontSize: 56, mb: 2 }} />
                <Typography
                  variant="h5"
                  sx={{ fontFamily: FONT_FAMILY, fontWeight: 700, mb: 1 }}
                >
                  Join {preview.provider.business_name}
                </Typography>
                <Typography sx={{ fontFamily: FONT_FAMILY, color: '#4A5568', mb: 3 }}>
                  This invite will connect your account to {preview.site.name}.
                </Typography>
                <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                  Invite expires {new Date(preview.expires_at).toLocaleString()}.
                </Alert>
                {preview.can_redeem === false && (
                  <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                    {getPreviewMessage()}
                  </Alert>
                )}
                {shouldPromptProfileLeave && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/profile')}
                    sx={{ mb: 1.5, borderRadius: '14px', py: 1.2 }}
                  >
                    Go to Profile
                  </Button>
                )}
                {preview.can_redeem !== false && (
                  <Box sx={{ textAlign: 'left', mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                        />
                      }
                      label={
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
                          I accept the{' '}
                          <Link
                            href="https://jeffsays.com/provider-terms.html"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Provider Terms &amp; Conditions
                          </Link>
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={acceptedPrivacy}
                          onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                        />
                      }
                      label={
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
                          I accept the{' '}
                          <Link
                            href="https://jeffsays.com/provider-privacy.html"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Provider Privacy Policy
                          </Link>
                        </Typography>
                      }
                    />
                  </Box>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleRedeem}
                  disabled={
                    redeeming
                    || preview.can_redeem === false
                    || !acceptedTerms
                    || !acceptedPrivacy
                  }
                  sx={{ borderRadius: '14px', py: 1.4, fontWeight: 700 }}
                >
                  {redeeming ? 'Joining...' : 'Join Provider'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => navigate('/home')}
                  sx={{ mt: 1.5 }}
                >
                  Not now
                </Button>
              </Box>
            ) : null}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default JoinPage;
